"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

import SearchInput from "@/components/flowbite/forms/fields/input/search-input";
import Typography from "@/components/flowbite/typography/typography";
import { CheckIcon, NavArrowDownIcon } from "@/components/icons-lib";
import { dictionaries } from "@/dictionaries/content";
import type { IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";
import { cn } from "@/shared/helpers/cn.helper";
import {
  filterDistrictSchoolsByName,
  groupDistrictSchoolsByType,
  SCHOOL_TYPE_SECTION_ORDER,
} from "@/shared/utils/group-district-schools-by-type.util";
import { SCHOOL_LEVEL_TYPE_LABELS } from "@/shared/utils/school-level-display.utils";

import {
  getDistrictSchoolSelectValue,
  type SchoolSelectValueKey,
} from "./school-select-value.util";

const EMPTY_SCHOOL_VALUE = "";

export interface GroupedSchoolSelectFieldProps {
  schools: readonly IDistrictSchoolListItem[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  noSchoolLabel?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  groupByType?: boolean;
  valueKey?: SchoolSelectValueKey;
}

interface SchoolSelectOptionButtonProps {
  school: IDistrictSchoolListItem;
  selected: boolean;
  valueKey: SchoolSelectValueKey;
  onSelect: (value: string) => void;
}

function SchoolSelectOptionButton({
  school,
  selected,
  valueKey,
  onSelect,
}: SchoolSelectOptionButtonProps) {
  const schoolValue = getDistrictSchoolSelectValue(school, valueKey);

  return (
    <button
      className={cn(
        "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-gray-100",
        selected && "bg-gray-50",
      )}
      type="button"
      onClick={() => onSelect(schoolValue)}
    >
      <Typography.BodyMMedium
        capitalize={false}
        className="min-w-0 flex-1 truncate text-text-primary"
      >
        {school.name ?? "Unnamed school"}
      </Typography.BodyMMedium>
      {selected ? (
        <CheckIcon className="h-4 w-4 shrink-0 text-btn-primary" />
      ) : null}
    </button>
  );
}

interface GroupedSchoolSelectPanelProps {
  isOpen: boolean;
  value: string;
  noSchoolLabel: string;
  searchPlaceholder: string;
  loading: boolean;
  schools: readonly IDistrictSchoolListItem[];
  schoolsByType: ReturnType<typeof groupDistrictSchoolsByType>;
  groupByType: boolean;
  valueKey: SchoolSelectValueKey;
  onSelect: (value: string) => void;
}

function GroupedSchoolSelectPanel({
  isOpen,
  value,
  noSchoolLabel,
  searchPlaceholder,
  loading,
  schools,
  schoolsByType,
  groupByType,
  valueKey,
  onSelect,
}: GroupedSchoolSelectPanelProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      return;
    }

    const frame = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const showNoSchoolOption = useMemo(() => {
    if (searchQuery.length === 0) {
      return true;
    }

    const normalized = searchQuery.toLowerCase().trim();
    return noSchoolLabel.toLowerCase().includes(normalized);
  }, [noSchoolLabel, searchQuery]);

  const flatSchools = useMemo(() => {
    const filtered = filterDistrictSchoolsByName(schools, searchQuery);
    return [...filtered].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [schools, searchQuery]);

  return (
    <>
      <div
        className="border-main-100 border-b p-2"
        onKeyDown={(event) => event.stopPropagation()}
      >
        <SearchInput
          ref={searchInputRef}
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onClear={() => setSearchQuery("")}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-1">
        {loading ? (
          <div className="text-light-300 px-3 py-2 text-sm">Loading schools...</div>
        ) : (
          <>
            {showNoSchoolOption ? (
              <button
                className={cn(
                  "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-gray-100",
                  value === EMPTY_SCHOOL_VALUE && "bg-gray-50",
                )}
                type="button"
                onClick={() => onSelect(EMPTY_SCHOOL_VALUE)}
              >
                <Typography.BodyMMedium
                  capitalize={false}
                  className="text-text-primary"
                >
                  {noSchoolLabel}
                </Typography.BodyMMedium>
                {value === EMPTY_SCHOOL_VALUE ? (
                  <CheckIcon className="h-4 w-4 text-btn-primary" />
                ) : null}
              </button>
            ) : null}

            {groupByType ? (
              SCHOOL_TYPE_SECTION_ORDER.map((sectionType) => {
                const sectionSchools = filterDistrictSchoolsByName(
                  schoolsByType[sectionType],
                  searchQuery,
                );

                if (sectionSchools.length === 0) {
                  return null;
                }

                return (
                  <div
                    key={sectionType}
                    className="mb-2"
                  >
                    <Typography.BodySRegular className="text-light-300 px-3 py-1">
                      {SCHOOL_LEVEL_TYPE_LABELS[sectionType]}
                    </Typography.BodySRegular>

                    {sectionSchools.map((school) => (
                      <SchoolSelectOptionButton
                        key={`${sectionType}-${school.id}`}
                        school={school}
                        selected={value === getDistrictSchoolSelectValue(school, valueKey)}
                        valueKey={valueKey}
                        onSelect={onSelect}
                      />
                    ))}
                  </div>
                );
              })
            ) : (
              flatSchools.map((school) => (
                <SchoolSelectOptionButton
                  key={school.id}
                  school={school}
                  selected={value === getDistrictSchoolSelectValue(school, valueKey)}
                  valueKey={valueKey}
                  onSelect={onSelect}
                />
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}

export function GroupedSchoolSelectField({
  schools,
  value,
  onChange,
  placeholder = "Select school",
  noSchoolLabel = "No school",
  searchPlaceholder = dictionaries.mapPage.plottingFilters.schoolType.searchSchools,
  loading = false,
  disabled = false,
  className,
  groupByType = true,
  valueKey = "internalId",
}: GroupedSchoolSelectFieldProps) {
  const schoolsByType = useMemo(() => groupDistrictSchoolsByType(schools), [schools]);

  const schoolNameByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const school of schools) {
      const key = getDistrictSchoolSelectValue(school, valueKey);
      if (key) {
        map.set(key, school.name ?? "Unnamed school");
      }
    }
    return map;
  }, [schools, valueKey]);

  const triggerContent = useMemo(() => {
    if (loading) {
      return "Loading...";
    }

    if (value === EMPTY_SCHOOL_VALUE) {
      return noSchoolLabel;
    }

    return schoolNameByValue.get(value) ?? placeholder;
  }, [loading, noSchoolLabel, placeholder, schoolNameByValue, value]);

  return (
    <Popover className={cn("relative min-w-0", className)}>
      {({ open, close }) => (
        <>
          <PopoverButton
            className={cn(
              "h-10 w-full",
              "flex items-center justify-between gap-3",
              "px-3 py-2",
              "bg-white",
              "rounded-xl",
              "border",
              "focus:outline-none",
              Typography.classes.Body.default,
              Typography.classes.Body.classnames.mMedium,
              "text-light-400",
              !open && !disabled && "border-main-100",
              open && !disabled && "border-light-600",
              disabled && "border-main-100 cursor-not-allowed bg-background-primary opacity-50",
            )}
            disabled={disabled || loading}
          >
            <span className="min-w-0 flex-1 truncate text-left">{triggerContent}</span>
            <NavArrowDownIcon
              className={cn(
                "flex-shrink-0 text-gray-400 transition-transform duration-200",
                open && "rotate-180",
              )}
              height={24}
              width={24}
            />
          </PopoverButton>

          <PopoverPanel
            anchor={false}
            className={cn(
              "border-main-100 z-10 mt-1 flex max-h-64 w-full flex-col overflow-hidden rounded-xl border bg-white shadow-[0_4px_10px_-4px_rgba(24,39,75,0.08)]",
            )}
          >
            <GroupedSchoolSelectPanel
              groupByType={groupByType}
              isOpen={open}
              loading={loading}
              noSchoolLabel={noSchoolLabel}
              schools={schools}
              schoolsByType={schoolsByType}
              searchPlaceholder={searchPlaceholder}
              value={value}
              valueKey={valueKey}
              onSelect={(selectedValue) => {
                onChange(selectedValue);
                close();
              }}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
